import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import { CHANGED_ATTRIBUTE } from "./change-diff";
import { buildPreviewDocument } from "./ScreenThumbnail";

const FRAME_MESSAGE = "kayros-change-frame";

const VIEWPORT = 390;

const ZOOM = 1.25;

const FALLBACK_FRAME_HEIGHT = 600;

const FRAME_MEASURE_DELAY_MS = 400;

const FRAME_PAN_X = 8;

const FRAME_PAN_Y = 32;

const CHANGE_MARK_VARIABLE = "--canvas-change-mark";

const changeMarkColor = () => {
  if (typeof window === "undefined" || typeof getComputedStyle !== "function")
    return "currentColor";
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(CHANGE_MARK_VARIABLE)
      .trim() || "currentColor"
  );
};

const frameAssets = ({ name, unpinned, markColor }) =>
  `<style>[${CHANGED_ATTRIBUTE}]{outline:2px solid ${markColor} !important;` +
  `outline-offset:3px;border-radius:4px;}html,body{overflow:hidden;}</style>` +
  `<script>(function(){function s(){` +
  (unpinned
    ? `document.querySelectorAll("body *").forEach(function(n){` +
      `var p=getComputedStyle(n).position;if(p==="fixed"||p==="sticky")n.style.display="none";});`
    : "") +
  `var b=document.body;if(!b)return;var t=document.querySelector("[${CHANGED_ATTRIBUTE}]");` +
  `var r=t?t.getBoundingClientRect():null;` +
  `parent.postMessage({type:"${FRAME_MESSAGE}",name:"${name}",` +
  `height:Math.ceil(Math.max(b.scrollHeight,b.getBoundingClientRect().height)),` +
  `x:r?r.left:null,y:r?r.top:null},"*");}` +
  `window.addEventListener("load",s);setTimeout(s,${FRAME_MEASURE_DELAY_MS});})();</script>`;

const documentOf = ({ html, styleContent, name, unpinned }) =>
  buildPreviewDocument({
    htmlContent: html || "",
    styleContent,
  }).replace(
    "</head>",
    `${frameAssets({ name, unpinned, markColor: changeMarkColor() })}</head>`,
  );

const frameHeight = (report) =>
  report ? report.height : FALLBACK_FRAME_HEIGHT;

const useFrameReport = (name) => {
  const [report, setReport] = useState(null);
  useEffect(() => {
    const listen = (event) => {
      const data = event.data || {};
      if (data.type !== FRAME_MESSAGE || data.name !== name) return;
      setReport({ height: data.height, x: data.x, y: data.y });
    };
    window.addEventListener("message", listen);
    return () => window.removeEventListener("message", listen);
  }, [name]);
  return report;
};

const useFrameBox = (frameRef, height, transform) => {
  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (isNil(frame)) return;
    frame.style.setProperty("--change-frame-width", `${VIEWPORT}px`);
    frame.style.setProperty("--change-frame-height", `${height}px`);
    if (!isNil(transform))
      frame.style.setProperty("--change-frame-transform", transform);
  }, [frameRef, height, transform]);
};

export const ChangeThumbnail = ({ name, html, styleContent, title }) => {
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const [width, setWidth] = useState(0);
  const report = useFrameReport(name);

  useEffect(() => {
    const box = boxRef.current;
    if (isNil(box)) return undefined;
    const measure = () => setWidth(box.clientWidth);
    measure();
    if (!window.ResizeObserver) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  const fitted = width ? Math.min(1, width / VIEWPORT) : 0;
  const marked = !isNil(report) && !isNil(report.x);
  const zoom = marked ? fitted * ZOOM : fitted;
  const left = marked ? Math.max(0, report.x * zoom - FRAME_PAN_X) : 0;
  const top = marked ? Math.max(0, report.y * zoom - FRAME_PAN_Y) : 0;
  useFrameBox(
    frameRef,
    frameHeight(report),
    `translate(${-left}px, ${-top}px) scale(${zoom})`,
  );

  return (
    <span ref={boxRef} className="canvas-claude__thumb-window">
      <iframe
        ref={frameRef}
        name={name}
        title={title}
        tabIndex={-1}
        sandbox="allow-scripts"
        srcDoc={documentOf({ html, styleContent, name, unpinned: true })}
        className="canvas-claude__thumb-frame"
      />
    </span>
  );
};

ChangeThumbnail.propTypes = {
  name: PropTypes.string.isRequired,
  html: PropTypes.string,
  styleContent: PropTypes.string,
  title: PropTypes.string,
};

ChangeThumbnail.defaultProps = {
  html: "",
  styleContent: "",
  title: "",
};

export const ChangeScreen = ({ name, html, styleContent, title }) => {
  const frameRef = useRef(null);
  const report = useFrameReport(name);
  useFrameBox(frameRef, frameHeight(report));
  return (
    <iframe
      ref={frameRef}
      name={name}
      title={title}
      sandbox="allow-scripts"
      srcDoc={documentOf({ html, styleContent, name, unpinned: false })}
      className="canvas-claude__compare-frame"
    />
  );
};

ChangeScreen.propTypes = ChangeThumbnail.propTypes;

ChangeScreen.defaultProps = ChangeThumbnail.defaultProps;
